// RecordingIndicator — компактный индикатор активной записи в шапке (Topbar),
// слева от кнопки тьютора. Виден при status ∈ {recording, paused, stopping}.
// Клик — возврат на /recordings/$id (live-overlay). "stopping" — отложенный
// finalize (upload-очередь ждёт сеть).
//
// Заменяет прежний sticky LiveBanner: тот рендерился в layout-потоке под шапкой,
// перекрывался Topbar/Sidebar (z-index) и сдвигал контент вниз (виден был только
// красный отблеск из-под меню). Индикатор внутри Topbar не занимает места в
// потоке и всегда виден рядом с остальными кнопками шапки.

import { Tooltip, TooltipContent, TooltipTrigger } from "@studyhub/ui/components/tooltip";
import { IconMicrophone } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useRecorderStore } from "@/stores/recorder";
import { formatDurationHms } from "./_format";

export function RecordingIndicator(): React.ReactElement | null {
	const { t } = useTranslation("recordings");
	const navigate = useNavigate();
	const status = useRecorderStore((s) => s.status);
	const recordingId = useRecorderStore((s) => s.recordingId);
	const elapsedMs = useRecorderStore((s) => s.elapsedMs);

	if (status !== "recording" && status !== "paused" && status !== "stopping") return null;
	const isRecording = status === "recording";

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<button
					type="button"
					onClick={() => {
						if (recordingId) {
							void navigate({
								to: "/recordings/$id",
								params: { id: recordingId },
							});
						} else {
							void navigate({ to: "/recordings/new" });
						}
					}}
					className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-[9px] border border-destructive/25 bg-destructive/10 px-2.5 text-destructive transition-colors hover:bg-destructive/15"
					aria-label={t("recorder.banner.return")}
				>
					<span className="relative inline-flex size-2">
						{isRecording && (
							<span className="absolute inset-0 rounded-full bg-destructive opacity-60 motion-safe:animate-ping" />
						)}
						<span
							className={`relative inline-flex size-2 rounded-full ${
								isRecording ? "bg-destructive" : "bg-warning"
							}`}
						/>
					</span>
					<IconMicrophone size={15} stroke={1.75} />
					<span className="text-xs font-semibold tabular-nums">
						{formatDurationHms(Math.floor(elapsedMs / 1000))}
					</span>
				</button>
			</TooltipTrigger>
			<TooltipContent side="bottom">
				{isRecording ? t("recorder.banner.recording") : t("recorder.banner.paused")}
			</TooltipContent>
		</Tooltip>
	);
}
